import type { MarketplaceCode, ProductReviewRow, ProductReviewThumbnail } from "./types";

export type ReviewThumbnailIdentity = { sellerSku: string; marketplaceCode: MarketplaceCode; asin: string };
export type ReviewThumbnailState = { status: "loading" | "loaded" | "error" | "empty"; url?: string; attempt: number };
type ThumbnailRefresh = boolean | number;
type ThumbnailLookup = (identity: ReviewThumbnailIdentity, refresh?: ThumbnailRefresh, signal?: AbortSignal) => Promise<string[]>;
const asinKey = (asin?: string | null) => asin?.trim().toUpperCase() ?? "";
const uniqueUrls = (urls: Array<string | null | undefined>) => [...new Set(urls.map(url => url?.trim()).filter((url): url is string => Boolean(url)))];

/** A country image is useful only when its ASIN is known to be the same product. */
export function reviewThumbnailCandidates(row: ProductReviewRow): string[] {
  const asin = asinKey(row.asin);
  return uniqueUrls([row.imageUrl, ...(row.marketplaces ?? []).filter(country => asin && asinKey(country.asin) === asin).map(country => country.imageUrl)]);
}

/** The queue shares catalog metadata, never image bytes or evidence for a review. */
export function createReviewThumbnailLookup(
  fetchMetadata: (identity: ReviewThumbnailIdentity, refresh: boolean) => Promise<ProductReviewThumbnail>,
  { cacheLimit = 200, now = Date.now }: { cacheLimit?: number; now?: () => number } = {},
): ThumbnailLookup {
  const cache = new Map<string, { urls: string[]; expires: number; generation?: number }>();
  type Subscriber = { resolve: (urls: string[]) => void; reject: (reason: unknown) => void; detach: () => void };
  type Work = { refresh: ThumbnailRefresh; subscribers: Set<Subscriber>; started: boolean; start: () => void };
  const pending = new Map<string, Work>();
  const queue: Work[] = [];
  let active = 0;
  const drain = () => { while (active < 2 && queue.length) queue.shift()!.start(); };
  const subscribe = (entry: Work, key: string, signal?: AbortSignal): Promise<string[]> => new Promise((resolve, reject) => {
    const subscriber = { resolve, reject, detach: () => signal?.removeEventListener("abort", abort) };
    const abort = () => {
      entry.subscribers.delete(subscriber); subscriber.detach(); reject(new DOMException("Vorschaubild wird nicht mehr benötigt.", "AbortError"));
      if (!entry.started && !entry.subscribers.size) {
        const index = queue.indexOf(entry); if (index >= 0) queue.splice(index, 1);
        if (pending.get(key) === entry) pending.delete(key);
      }
    };
    entry.subscribers.add(subscriber);
    signal?.addEventListener("abort", abort, { once: true });
    if (signal?.aborted) abort();
  });
  return (target, refresh = false, signal) => {
    if (signal?.aborted) return Promise.reject(new DOMException("Vorschaubild wird nicht mehr benötigt.", "AbortError"));
    const identity = { ...target, asin: asinKey(target.asin) };
    const key = `${identity.marketplaceCode}:${identity.asin}`;
    if (!identity.asin) return Promise.resolve([]);
    const token = typeof refresh === "number" && refresh > 0 ? refresh : undefined;
    const refreshing = refresh === true || token !== undefined;
    const inFlight = pending.get(key);
    if (inFlight && (!refreshing || inFlight.refresh === refresh)) return subscribe(inFlight, key, signal);
    const cached = cache.get(key);
    if (cached && cached.expires > now() && (!refreshing || token !== undefined && cached.generation === token)) {
      cache.delete(key); cache.set(key, cached);
      return Promise.resolve([...cached.urls]);
    }
    const generation = token ?? cached?.generation ?? (typeof inFlight?.refresh === "number" ? inFlight.refresh : undefined);
    cache.delete(key);
    const entry: Work = { refresh, subscribers: new Set(), started: false, start: () => {
      entry.started = true;
      active++;
      Promise.resolve().then(() => fetchMetadata(identity, refreshing)).then(result => {
        if (result.sellerSku !== identity.sellerSku || result.marketplaceCode !== identity.marketplaceCode || asinKey(result.asin) !== identity.asin || !Array.isArray(result.imageUrls) || result.imageUrls.some(url => typeof url !== "string")) {
          throw new Error("Die Identität des Vorschaubilds hat sich geändert.");
        }
        const urls = uniqueUrls(result.imageUrls);
        if (pending.get(key) === entry) {
          cache.set(key, { urls, generation, expires: now() + (urls.length ? 6 * 60 * 60 * 1000 : 5 * 60 * 1000) });
          while (cache.size > Math.max(1, cacheLimit)) cache.delete(cache.keys().next().value!);
        }
        return [...urls];
      }).then(urls => { finish(); entry.subscribers.forEach(subscriber => subscriber.resolve([...urls])); }, reason => { finish(); entry.subscribers.forEach(subscriber => subscriber.reject(reason)); });
      const finish = () => {
        if (pending.get(key) === entry) pending.delete(key);
        entry.subscribers.forEach(subscriber => subscriber.detach());
        active--; drain();
      };
    } };
    pending.set(key, entry);
    const result = subscribe(entry, key, signal);
    queue.push(entry);
    drain();
    return result;
  };
}

/** Each DOM event and asynchronous response belongs to one explicit attempt. */
export class ReviewThumbnailController {
  state: ReviewThumbnailState;
  private active = true;
  private near = false;
  private generation = 0;
  private metadataStarted = false;
  private metadataComplete = false;
  private failedUrls = new Set<string>();
  private metadataRequest?: AbortController;
  private candidates: string[];
  constructor(
    private identity: ReviewThumbnailIdentity,
    private snapshots: string[],
    private lookup: ThumbnailLookup,
    private changed: (state: ReviewThumbnailState) => void,
    private refresh: ThumbnailRefresh = false,
  ) {
    this.candidates = uniqueUrls(snapshots);
    this.state = { status: this.candidates.length || identity.asin ? "loading" : "empty", url: this.candidates[0], attempt: 0 };
  }
  private update(state: ReviewThumbnailState) { this.state = state; this.changed(state); }
  private next() {
    const url = this.candidates.find(candidate => !this.failedUrls.has(candidate));
    if (url) { this.update({ status: "loading", url, attempt: this.state.attempt + 1 }); return; }
    this.update({ status: this.metadataComplete || !this.identity.asin ? this.failedUrls.size ? "error" : "empty" : "loading", attempt: this.state.attempt + 1 });
    this.loadMetadata();
  }
  private loadMetadata() {
    if (!this.active || !this.near || this.state.url || !this.identity.asin || this.metadataStarted) return;
    this.metadataStarted = true;
    const generation = this.generation;
    this.metadataRequest = new AbortController();
    void this.lookup(this.identity, this.refresh, this.metadataRequest.signal).then(urls => {
      if (!this.active || generation !== this.generation) return;
      this.metadataComplete = true;
      this.candidates = uniqueUrls(urls);
      this.next();
    }).catch(() => {
      if (this.active && generation === this.generation) this.update({ status: "error", attempt: this.state.attempt + 1 });
    });
  }
  nearViewport() { if (this.active) { this.near = true; this.loadMetadata(); } }
  loaded(url: string | undefined, attempt: number, width: number, height: number) {
    if (!this.current(url, attempt)) return;
    if (!(width > 0) || !(height > 0)) { this.failed(url, attempt); return; }
    this.update({ ...this.state, status: "loaded" });
  }
  failed(url: string | undefined, attempt: number) {
    if (!this.current(url, attempt)) return;
    this.failedUrls.add(url!);
    this.next();
  }
  private current(url: string | undefined, attempt: number) {
    return this.active && this.state.status === "loading" && Boolean(url) && this.state.url === url && this.state.attempt === attempt;
  }
  retry() {
    if (!this.active) return;
    this.generation++;
    this.metadataRequest?.abort();
    this.refresh = true;
    this.failedUrls.clear(); this.metadataStarted = false; this.metadataComplete = false;
    this.candidates = uniqueUrls(this.snapshots);
    this.next();
  }
  dispose() { this.active = false; this.generation++; this.metadataRequest?.abort(); }
}
